import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography, IconButton } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useInstatDesign } from './theme/instatDesign';
import ClipCard from './ClipCard';

// Netflix-style horizontal scroller with peek-arrows on hover. Used by
// ClipsTab (date buckets) and InformeClipsSection (tag groups).
export default function ClipShelf({
  label,
  badge,           // optional ReactNode rendered next to the label (color chip etc.)
  clips,
  onPlay,
  onDelete,
  t,
  cardWidth,       // optional override forwarded to ClipCard
  sx,
}) {
  const d = useInstatDesign();
  const scrollRef = useRef(null);
  const [hover, setHover] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows, clips.length]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.round(el.clientWidth * 0.85) * dir;
    el.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <Box
      sx={{ mb: 2.5, position: 'relative', ...sx }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {(label || badge) && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, px: 0.5 }}>
          {badge}
          {label && <Typography sx={{ ...d.typo.title, fontSize: 15 }}>{label}</Typography>}
          <Typography sx={{ ...d.typo.caption }}>
            {clips.length}
          </Typography>
        </Stack>
      )}

      <Box sx={{ position: 'relative' }}>
        {canLeft && (
          <ScrollArrow side="left" visible={hover} onClick={() => scrollBy(-1)} t={t} d={d} />
        )}
        {canRight && (
          <ScrollArrow side="right" visible={hover} onClick={() => scrollBy(1)} t={t} d={d} />
        )}

        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 1.25,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            pb: 1,
            px: 0.5,
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {clips.map((clip) => (
            <ClipCard
              key={clip.id}
              clip={clip}
              onPlay={onPlay}
              onDelete={onDelete}
              t={t}
              width={cardWidth}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ScrollArrow({ side, visible, onClick, t, d }) {
  return (
    <IconButton
      onClick={onClick}
      aria-label={t ? t(side === 'left' ? 'instat.clips.scrollLeft' : 'instat.clips.scrollRight') : side}
      sx={{
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [side]: 4,
        zIndex: 2,
        width: 36,
        height: 36,
        bgcolor: d.colors.surface,
        boxShadow: d.shadows.cardStrong,
        opacity: visible ? 1 : 0,
        transition: `opacity ${d.motion.fast}`,
        '&:hover': { bgcolor: d.colors.surface },
      }}
    >
      {side === 'left' ? <ChevronLeftRoundedIcon /> : <ChevronRightRoundedIcon />}
    </IconButton>
  );
}
